import React, { useState, useEffect } from 'react';
import Button from 'components/ui/Buttons';
import Card from 'components/ui/Card';
import Dialog from 'components/ui/Dialog';
import { 
  FaDownload, 
  FaTimes, 
  FaMobile, 
  FaDesktop,
  FaInfoCircle 
} from 'react-icons/fa';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    // Verificar se o app já está instalado
    const checkIfInstalled = () => {
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return true;
      }
      
      // Verificar no localStorage se foi instalado
      const installed = localStorage.getItem('pwa-installed');
      if (installed) {
        setIsInstalled(true);
        return true;
      }
      
      return false;
    };

    // Capturar o evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Mostrar o prompt de instalação após 3 segundos
      setTimeout(() => {
        if (!checkIfInstalled()) {
          setShowInstallPrompt(true);
        }
      }, 3000);
    };

    // Verificar se o app foi instalado
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      localStorage.setItem('pwa-installed', 'true');
    };

    // Verificar se o app foi instalado no carregamento
    checkIfInstalled();

    // Adicionar event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Se não há prompt nativo, mostrar instruções manuais
      setShowInfo(true);
      return;
    }

    try {
      // Mostrar o prompt de instalação nativo
      deferredPrompt.prompt();
      
      // Aguardar a resposta do usuário
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('Usuário aceitou a instalação');
        setIsInstalled(true);
        setShowInstallPrompt(false);
        localStorage.setItem('pwa-installed', 'true');
      } else {
        console.log('Usuário recusou a instalação');
      }
      
      // Limpar o prompt
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
      setShowInfo(true);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Não mostrar novamente por 24 horas
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  const handleCloseInfo = () => {
    setShowInfo(false);
  };

  // Não mostrar se já está instalado ou se foi descartado recentemente
  if (isInstalled) return null;

  const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
  if (dismissedTime && Date.now() - parseInt(dismissedTime) < 24 * 60 * 60 * 1000) {
    return null;
  }

  return (
    <>
      {/* Prompt de Instalação */}
      {showInstallPrompt && (
        <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-80">
          <Card className="bg-white shadow-lg border border-gray-200">
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <FaDownload className="text-teal-500 text-xl mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800">
                    Instalar DrDerma
                  </h3>
                </div>
                <button
                  onClick={handleDismiss}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Instale o DrDerma no seu dispositivo para acesso rápido e funcionalidades offline.
              </p>
              
              <div className="flex space-x-2">
                <Button
                  variant="solid"
                  size="sm"
                  onClick={handleInstallClick}
                  className="flex-1"
                >
                  <FaDownload className="mr-2" />
                  Instalar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleDismiss}
                >
                  Agora não
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de Instruções Manuais */}
      <Dialog
        isOpen={showInfo}
        onClose={handleCloseInfo}
        title="Como instalar o DrDerma"
        size="md"
      >
        <div className="space-y-4">
          <div className="text-center mb-4">
            <FaInfoCircle className="text-teal-500 text-4xl mx-auto mb-2" />
            <p className="text-gray-600">
              Siga as instruções abaixo para instalar o DrDerma no seu dispositivo:
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-teal-100 text-teal-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">
                  <FaMobile className="inline mr-2" />
                  No Android (Chrome)
                </h4>
                <p className="text-gray-600 text-sm">
                  Toque no menu (⋮) → "Adicionar à tela inicial" → "Adicionar"
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-teal-100 text-teal-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">
                  <FaMobile className="inline mr-2" />
                  No iPhone (Safari)
                </h4>
                <p className="text-gray-600 text-sm">
                  Toque no botão de compartilhar (□↑) → "Adicionar à Tela Inicial" → "Adicionar"
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-teal-100 text-teal-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">
                  <FaDesktop className="inline mr-2" />
                  No Desktop (Chrome/Edge)
                </h4>
                <p className="text-gray-600 text-sm">
                  Clique no ícone de instalação (⬇) na barra de endereços ou no menu → "Instalar DrDerma"
                </p>
              </div>
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mt-4">
            <p className="text-teal-800 text-sm">
              <strong>Benefícios:</strong> Acesso offline, notificações push, ícone na tela inicial e experiência de app nativo.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default PWAInstallPrompt; 